import { ApiError, api } from "../api";
import { presetFor } from "../avatar/catalog";
import { avatarPartsReady, drawFrame } from "../avatar/composer";
import { AVATAR_FRAME } from "../config";
import type { Session } from "../session";
import type { PublicUser } from "../types";
import { clearFieldErrors, showFieldErrors } from "./AuthOverlay";
import { requireElement } from "./Hud";

const PREVIEW_SCALE = 2;

type Tab = "avatar" | "name" | "password";

export interface ProfileMenuResult {
  session: Session;
  /** true se avatar ou nome publico mudaram — o mundo precisa reconectar pra refletir. */
  changed: boolean;
  /** true quando o jogador clicou em "Sair" — o chamador deve encerrar a sessao. */
  leave: boolean;
  /** true quando pediu para editar o avatar — o chamador abre o customizador. */
  customize: boolean;
}

/**
 * Menu de perfil: abrir o customizador de avatar, trocar nome publico e senha. Resolve quando o
 * jogador fecha o painel, com a sessao (possivelmente atualizada).
 *
 * onLiveUpdate da um retorno visual imediato no HUD (o botao de perfil no
 * canto) assim que avatar/nome mudam; o mundo em si (o avatar dos outros
 * jogadores, o nome no chat) so pega a mudanca quando o chamador reconecta,
 * o que acontece ao fechar o menu se algo mudou.
 */
export function showProfileMenu(session: Session, onLiveUpdate: (user: PublicUser) => void): Promise<ProfileMenuResult> {
  return new Promise((resolve) => {
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = template(session.user.username, session.user.emailVerified);
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(selector: string) => overlay.querySelector(selector) as T;

    let user = session.user;
    let changed = false;
    let leave = false;
    let customize = false;

    const notice = $<HTMLParagraphElement>(".notice");
    const setNotice = (text: string | null, level: "ok" | "error" | "info" = "info") => {
      if (!text) {
        notice.classList.add("hidden");
        return;
      }
      notice.className = `notice ${level === "info" ? "" : level}`.trim();
      notice.textContent = text;
      notice.classList.remove("hidden");
    };

    const tabButtons = {
      avatar: $<HTMLButtonElement>('[data-tab="avatar"]'),
      name: $<HTMLButtonElement>('[data-tab="name"]'),
      password: $<HTMLButtonElement>('[data-tab="password"]'),
    };
    const tabPanels = {
      avatar: $<HTMLDivElement>("#tab-avatar"),
      name: $<HTMLDivElement>("#tab-name"),
      password: $<HTMLDivElement>("#tab-password"),
    };
    const setTab = (tab: Tab) => {
      for (const key of Object.keys(tabButtons) as Tab[]) {
        tabButtons[key].setAttribute("aria-selected", String(key === tab));
        tabPanels[key].classList.toggle("hidden", key !== tab);
      }
      clearFieldErrors(overlay);
      setNotice(null);
    };
    (Object.keys(tabButtons) as Tab[]).forEach((tab) => {
      tabButtons[tab].addEventListener("click", () => setTab(tab));
    });

    const close = () => {
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve({ session: { ...session, user }, changed, leave, customize });
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    $<HTMLButtonElement>("#profile-close").addEventListener("click", close);
    $<HTMLButtonElement>("#profile-leave").addEventListener("click", () => {
      leave = true;
      close();
    });

    const applyUpdate = (updated: PublicUser) => {
      user = updated;
      changed = true;
      onLiveUpdate(user);
    };

    // ------------------------------------------------- confirmacao de e-mail
    const verifyBtn = overlay.querySelector<HTMLButtonElement>("#profile-verify");
    verifyBtn?.addEventListener("click", async () => {
      verifyBtn.disabled = true;
      try {
        const result = await api.resendVerification(session.token);
        setNotice(result.message, "ok");
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível reenviar o link", "error");
        verifyBtn.disabled = false;
      }
    });

    // ------------------------------------------------------------- avatar
    // A edicao acontece no customizador (tela propria); aqui so a previa.
    const preview = $<HTMLCanvasElement>("#profile-avatar");
    preview.width = AVATAR_FRAME.width;
    preview.height = AVATAR_FRAME.height;
    preview.style.width = `${AVATAR_FRAME.width * PREVIEW_SCALE}px`;
    preview.style.height = `${AVATAR_FRAME.height * PREVIEW_SCALE}px`;
    if (avatarPartsReady()) drawFrame(preview, user.appearance ?? presetFor(user.skin), 0);

    $<HTMLButtonElement>("#profile-customize").addEventListener("click", () => {
      customize = true;
      close();
    });

    // --------------------------------------------------------------- nome
    const nameForm = $<HTMLFormElement>("#form-name");
    const nameInput = nameForm.elements.namedItem("displayName") as HTMLInputElement;
    nameInput.value = user.displayName;

    nameForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);
      const submit = nameForm.querySelector("button.primary") as HTMLButtonElement;
      submit.disabled = true;
      try {
        const result = await api.updateDisplayName(session.token, nameInput.value.trim());
        applyUpdate(result.user);
        nameInput.value = user.displayName;
        setNotice("Nome público atualizado!", "ok");
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          showFieldErrors(overlay, error.fields);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível salvar o nome", "error");
        }
      } finally {
        submit.disabled = false;
      }
    });

    // -------------------------------------------------------------- senha
    const passwordForm = $<HTMLFormElement>("#form-password");
    passwordForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);
      const submit = passwordForm.querySelector("button.primary") as HTMLButtonElement;
      const currentPassword = (passwordForm.elements.namedItem("currentPassword") as HTMLInputElement).value;
      const newPassword = (passwordForm.elements.namedItem("newPassword") as HTMLInputElement).value;
      const confirmPassword = (passwordForm.elements.namedItem("confirmPassword") as HTMLInputElement).value;

      if (newPassword !== confirmPassword) {
        showFieldErrors(overlay, { confirmPassword: "As senhas não coincidem" });
        return;
      }

      submit.disabled = true;
      try {
        const result = await api.updatePassword(session.token, currentPassword, newPassword);
        setNotice(result.message, "ok");
        passwordForm.reset();
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          showFieldErrors(overlay, error.fields);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível trocar a senha", "error");
        }
      } finally {
        submit.disabled = false;
      }
    });
  });
}

function template(username: string, emailVerified: boolean): string {
  return `
    <div class="panel">
      <div class="profile-header">
        <div>
          <h1>Perfil</h1>
          <p class="sub">Logado como <strong>${username}</strong></p>
          ${
            emailVerified
              ? ""
              : `<p class="sub verify-line">E-mail ainda não confirmado (opcional).
                 <button type="button" id="profile-verify" class="link-btn">reenviar link</button></p>`
          }
        </div>
        <button type="button" id="profile-leave" class="secondary danger small">Sair</button>
      </div>

      <p class="notice hidden"></p>

      <div class="tabs" role="tablist">
        <button type="button" role="tab" data-tab="avatar" aria-selected="true">Avatar</button>
        <button type="button" role="tab" data-tab="name" aria-selected="false">Nome</button>
        <button type="button" role="tab" data-tab="password" aria-selected="false">Senha</button>
      </div>

      <div id="tab-avatar">
        <div class="profile-avatar-preview">
          <canvas id="profile-avatar" aria-label="Seu avatar"></canvas>
          <div>
            <p class="sub" style="margin-bottom:12px">
              Troque cabelo, roupa, cores e o tom de pele do seu avatar.
            </p>
            <button type="button" id="profile-customize" class="primary">Editar avatar</button>
          </div>
        </div>
      </div>

      <div id="tab-name" class="hidden">
        <form id="form-name" novalidate>
          <div class="field">
            <label for="profile-name">Nome público</label>
            <input id="profile-name" name="displayName" type="text" maxlength="20" autocomplete="off" />
          </div>
          <button type="submit" class="primary">Salvar nome</button>
        </form>
      </div>

      <div id="tab-password" class="hidden">
        <form id="form-password" novalidate>
          <div class="field">
            <label for="profile-current-password">Senha atual</label>
            <input id="profile-current-password" name="currentPassword" type="password"
                   autocomplete="current-password" required />
          </div>
          <div class="field">
            <label for="profile-new-password">Nova senha (8+ caracteres, com letras e números)</label>
            <input id="profile-new-password" name="newPassword" type="password"
                   autocomplete="new-password" minlength="8" required />
          </div>
          <div class="field">
            <label for="profile-confirm-password">Confirme a nova senha</label>
            <input id="profile-confirm-password" name="confirmPassword" type="password"
                   autocomplete="new-password" minlength="8" required />
          </div>
          <button type="submit" class="primary">Trocar senha</button>
        </form>
      </div>

      <button type="button" id="profile-close" class="secondary" style="margin-top:16px">Fechar</button>
    </div>
  `;
}
